import { IconFont, Loading, pointer, Toast } from '@byecode/ui'
import { useAbortAll, useItemErrorListener, useItemFinishListener, useItemStartListener, useUploadyContext } from '@rpldy/shared-ui'
import UploadDropZone from '@rpldy/upload-drop-zone'
import { useRequestPreSend } from '@rpldy/uploady'
import React, { useCallback, useMemo, useState } from 'react'
import styled from 'styled-components'

import { avatarMaxUploadSizeMB } from '../../constants'
import type { AvatarSize } from '../../types'
import { tokenStorage } from '../../utils/token'
import { useUploadTokenRefresh } from '../UploadManage/hooks/useUploadTokenRefresh'
import { useTokenRefreshManager } from '../UploadManage/TokenRefreshManagerContext'
import { UserAvatar } from '../UserAvatar'

const SCxContainer = styled.div`
    position: relative;
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background-color: var(--color-gray-100);
    display: flex;
    justify-content: center;
    align-items: center;
    ${pointer}
`

const SCxImageWrapper = styled.div`
    width: 100%;
    height: 100%;
    border-radius: 50%;
    border: 1px solid var(--color-gray-200);
    overflow: hidden;
`

const SCxClear = styled.div`
    position: absolute;
    right: 0;
    top: 0;
    width: 20px;
    height: 20px;
    border-radius: 50%;
    background-color: var(--color-gray-100);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1;
    ${pointer}
`

interface UploadUserAvatarProps {
    children?: React.ReactNode
    id?: string
    size?: AvatarSize
    onUpdateImg: (imgSrc: string) => void
    name?: string
    img?: string
    fontSize?: number
    disable?: boolean
}
const fileSizeLimitMB = avatarMaxUploadSizeMB
export const UploadUserAvatar: React.FC<UploadUserAvatarProps> = ({
    id = '',
    img,
    size,
    onUpdateImg,
    name = '',
    disable = false,
    fontSize
}) => {
    const abortAll = useAbortAll()
    const uploady = useUploadyContext()
    const tokenRefreshManager = useTokenRefreshManager()
    const [loading, setLoading] = useState(false)

    // 动态注入 Authorization token
    useRequestPreSend(({ items, options }) => {
        const token = tokenStorage.getAccessToken()

        return {
            items,
            options: {
                ...options,
                destination: {
                    ...options.destination,
                    headers: {
                        ...options.destination?.headers,
                        ...(token ? { Authorization: `Bearer ${token}` } : {})
                    }
                }
            }
        }
    })

    // 处理 token 刷新后的重试
    useUploadTokenRefresh()

    useItemStartListener(item => {
        const { file } = item
        if (file.size > fileSizeLimitMB * 1024 * 1024) {
            Toast.warning(`文件 "${file.name}" 超过了 ${fileSizeLimitMB}MB 的限制`)
            abortAll()
        }
        setLoading(true)
    })

    useItemFinishListener(item => {
        const { uploadResponse } = item
        setLoading(false)
        onUpdateImg(uploadResponse.data.content.url)
    })

    useItemErrorListener(async item => {
        // 🔑 处理 401 错误
        if (item.uploadStatus === 401) {
            if (!tokenRefreshManager) {
                setLoading(false)
                Toast.error('认证失败，请重新登录')
                return
            }

            try {
                // 刷新 token
                await tokenRefreshManager.handleUpload401(item.id)
                // 触发重试（通过 uploadManageEvents）
                const { uploadManageEvents } = await import('../UploadManage/hooks/events')
                uploadManageEvents.emit('retry-item', { itemId: item.id })
            } catch {
                setLoading(false)
                Toast.error('上传失败，请重试')
            }
            return
        }

        // 其他错误
        setLoading(false)
        Toast.error('上传失败，请重试')
    })

    const handleClear = useCallback(
        (e: React.MouseEvent) => {
            e.stopPropagation()
            onUpdateImg('')
        },
        [onUpdateImg]
    )

    const handleUpload = useCallback(() => {
        if (disable) {
            return
        }
        uploady.showFileUpload()
    }, [disable, uploady])

    const content = useMemo(() => {
        if (loading) {
            return <Loading outlined />
        }
        if (img) {
            return (
                <>
                    <SCxImageWrapper>
                        <UserAvatar borderColor="" id={id} avatar={img} name={name} size={size} fontSize={fontSize} />
                    </SCxImageWrapper>
                    <SCxClear onClick={handleClear}>
                        <IconFont type="Close" size={16} fill="var(--color-gray-500)" />
                    </SCxClear>
                </>
            )
        }
        return <IconFont type="Camera" size={24} fill="var(--color-gray-400)" />
    }, [fontSize, handleClear, id, img, loading, name, size])

    return (
        <UploadDropZone>
            <SCxContainer onClick={handleUpload}>{content}</SCxContainer>
        </UploadDropZone>
    )
}
